import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Form } from '@ant-design/compatible';
import { QuestionCircleOutlined } from '@ant-design/icons';
import '@ant-design/compatible/assets/index.css';
import {
  Row,
  Col,
  Select,
  Checkbox,
  Input,
  InputNumber,
  Radio,
  Tooltip,
  DatePicker,
  Icon,
  Divider,
} from 'antd';
import { toLower } from 'lodash';

import '@ant-design/compatible/assets/index.css';

import { getSymbolFromCurrency } from '~helpers/currency';
import {
  rankTypeOptions,
  billingCycleOptions,
  displayLocaleOptions,
} from '~constants/property-field-options';
import { propertyState } from '~constants';
import {
  propertyStatus,
  propertyTenancyAgreementUnit,
  propertyTenancyAgreementField,
  propertyTenancyAgreementTimeType,
  propertyTenancyAgreementCalculateType,
} from '~constants/listing-management';
import { propertyDraftCategory, propertyDraftStatus } from '~constants/pending-approval';
import { generateSlug } from '~helpers';
import { bookingJourneyType } from 'common/constants/booking';
import {
  depositTransferTime,
  rentTransferTime,
  apCategoryEnum,
  allPaymentMethod,
  PaymentPlanCategory,
  apCategoryType,
} from '~constants/landlord';
import enhanceForm from '~hocs/enhance-form';
import * as queries from '~settings/queries';
import { fetch } from '~helpers/graphql';
import { getHtmlLength, htmlMinify, checkSlugExist } from '~helpers/property-edit';
import { cancellationPeriodTypeValue } from '~constants/property-field-options';
import { getBillingCycleText, getRankSettingValue } from '~helpers/property-field-option';

import ReactQuill from '~components/react-quill';
import PropertyDetailWrapper from '~components/property-detail-wrapper';
import { onlyViewPropertyRole } from '~client/helpers/auth';
import SelectBookingType from 'common/components/booking-type-select';
import { dayFormatType, moment, formatDay } from '~helpers/day';
import CancellationBlock from '../cancellation-block';
import { getSafeHtml } from 'common/helpers/safeHtml';

const FormItem = Form.Item;
const { TextArea } = Input;

@enhanceForm()
export default class PropertyDetail extends React.Component {
  constructor(props) {
    super(props);

    this.mergedProperty = this.mergeDraft();
    this.rankSettings = getRankSettingValue({ ...this.mergedProperty });
    this.propertyTenancyAgreementCalculateTypeList = Object.keys(
      propertyTenancyAgreementCalculateType,
    );
    this.propertyTenancyAgreementParams = ['calculateType', 'field', 'timeType', 'unit', 'value'];
  }

  componentDidMount() {
    this.props.onRef(this);
  }

  componentWillUnmount() {
    this.props.onRef(undefined);
  }

  mergeDraft = () => {
    const { property } = this.props;
    // unpublished property always show raw data
    if (
      property &&
      property.drafts &&
      property.drafts.edges.length > 0 &&
      property.status === propertyStatus.PUBLISHED
    ) {
      const propertyDrafts = property.drafts.edges.map(draft => draft.node);
      const detailDraft = propertyDrafts.find(draft => {
        const { category, status } = draft;
        return category === propertyDraftCategory.DETAIL && status === propertyDraftStatus.PENDING;
      });

      if (detailDraft) {
        const data = Object.assign({}, property, detailDraft.changes);

        return data;
      }

      return property;
    }

    return property;
  };

  quillCounter = html => {
    const number = getHtmlLength(html);
    return <div className={classNames('property-detail__counter')}>{number}</div>;
  };

  getCityCountry = () => {
    const cityArr = [];

    if (this.mergedProperty.city) {
      cityArr.push(this.mergedProperty.city.name);

      if (this.mergedProperty.city.country) {
        cityArr.push(this.mergedProperty.city.country.name);
      }
    }

    return cityArr.join();
  };

  onNameBlur = e => {
    if (e.target.value && !this.props.form.getFieldValue('slug')) {
      const slug = generateSlug(e.target.value);
      this.props.form.setFields({ slug: { value: slug, touched: true } });
      this.props.form.validateFields(['slug']);
    }
  };

  validateSlug = (_rule, value, callback) => {
    if (!value || value === this.props.property.slug) {
      callback();
    } else if (!/^[A-Za-z0-9\-]+$/.test(value)) {
      // eslint-disable-line
      callback(this.props.t('cms.properties.edit.detail.property_slug.format_error'));
    } else {
      checkSlugExist(value, callback, () => {
        callback(this.props.t('cms.properties.edit.detail.property_slug.exist_error'));
      });
    }
  };

  textAreaCounter = (text, limit) => {
    const number = text && text.trim() ? text.trim().length : 0;
    return (
      <div
        className={classNames('property-detail__counter', {
          'property-detail__counter--red': number > limit,
        })}
      >
        {`${number}/${limit}`}
      </div>
    );
  };

  isStripePayment = property => {
    return property?.withdrawMthod?.methodType === allPaymentMethod.STRIPE;
  };

  isDepositOnly = property => {
    return (
      property?.paymentPlan?.category === PaymentPlanCategory.DEPOSIT_IS_REQUIRED &&
      property?.apCategory === apCategoryType.KEY
    );
  };

  requestParams = changeFields => {
    let internalFieldsParams = { profile: {} };

    if (this.isStripePayment(this.mergedProperty) || this.isDepositOnly(this.mergedProperty)) {
      delete changeFields.apCategory;
    }

    const detailsParams = Object.assign({}, changeFields);

    [
      'rank',
      'rankType',
      'bookingJourney',
      'apCategory',
      'depositTransferTime',
      'rentTransferTime',
      'displaySettings',
    ].forEach(key => {
      if (key in changeFields) {
        internalFieldsParams[key] = changeFields[key];

        delete detailsParams[key];
      }
    });

    if ('rankTypeCn' in changeFields) {
      let profile = {};
      if (changeFields.rankTypeCn === 'NORMAL') {
        profile = { starCnValue: null, starCnExpiredAt: null, blacksheepCn: false };
      }
      if (changeFields.rankTypeCn === 'STAR') {
        profile = {
          starCnValue: changeFields['starCnValue'],
          starCnExpiredAt: changeFields['starCnExpiredAt']
            ? formatDay(dayFormatType.FILTERPARAMS, changeFields['starCnExpiredAt'])
            : null,
          blacksheepCn: false,
        };
      }
      if (changeFields.rankTypeCn === 'BLACKSHEEP') {
        profile = { starCnValue: null, starCnExpiredAt: null, blacksheepCn: true };
      }
      internalFieldsParams.profile = Object.assign(internalFieldsParams.profile, profile);
      ['rankTypeCn', 'starCnValue', 'starCnExpiredAt'].forEach(item => {
        if (item in changeFields) {
          delete detailsParams[item];
        }
      });
    }

    if ('rankTypeRow' in changeFields) {
      let profile = {};
      if (changeFields.rankTypeRow === 'NORMAL') {
        profile = { starRowValue: null, starRowExpiredAt: null, blacksheepRow: false };
      }
      if (changeFields.rankTypeRow === 'STAR') {
        profile = {
          starRowValue: changeFields['starRowValue'],
          starRowExpiredAt: changeFields['starRowExpiredAt']
            ? formatDay(dayFormatType.FILTERPARAMS, changeFields['starRowExpiredAt'])
            : null,
          blacksheepRow: false,
        };
      }
      if (changeFields.rankTypeRow === 'BLACKSHEEP') {
        profile = { starRowValue: null, starRowExpiredAt: null, blacksheepRow: true };
      }
      internalFieldsParams.profile = Object.assign(internalFieldsParams.profile, profile);
      ['rankTypeRow', 'starRowValue', 'starRowExpiredAt'].forEach(item => {
        if (item in changeFields) {
          delete detailsParams[item];
        }
      });
    }

    // Without this value backend will not update displaySettings.
    if ('displaySettings' in changeFields) {
      internalFieldsParams['updateDisplaySetting'] = true;
    }

    return { internalFieldsParams, detailsParams };
  };

  beforeSave = callback => {
    this.props.checkDraftReviewmodal(() => this.handleSubmit(callback));
  };

  handleSubmit = (callback = () => {}) => {
    this.props.form.validateFieldsAndScroll(
      { scroll: { offsetTop: 76, offsetBottom: 76 } },
      (err, formValues) => {
        if (!err) {
          const { changeFields, property } = this.props;
          const requests = [];
          const isSemiOrInstant =
            this.mergedProperty &&
            (this.mergedProperty.bookingJourney === 'SEMI_AUTOMATIC' ||
              this.mergedProperty.bookingJourney === 'INSTANT_BOOK');
          if (isSemiOrInstant && !this.mergedProperty.cancellationPeriodType) {
            changeFields.cancellationPeriodType = cancellationPeriodTypeValue.BEFORE_MOVE_IN;
            changeFields.cancellationPeriodValue = 30;
          }

          const isCreateAction = !property.tenancyAgreement;
          let hasTenancyAgreementChanged = false;
          const propertyTenancyAgreementChangeParams = {};
          if (isSemiOrInstant) {
            // Call update/create property tenancy agreement if needed.
            this.propertyTenancyAgreementParams.forEach(param => {
              const key = `tenancyAgreement${param.charAt(0).toUpperCase() + param.slice(1)}`;
              if (!(key in changeFields)) {
                return;
              }

              if (param === 'timeType') {
                propertyTenancyAgreementChangeParams[param] = changeFields[key]
                  ? propertyTenancyAgreementTimeType.FIXED
                  : propertyTenancyAgreementTimeType.ANYTIME;
              } else {
                propertyTenancyAgreementChangeParams[param] = changeFields[key];
              }
              delete changeFields[key];
              hasTenancyAgreementChanged = true;
            });
          }

          const { internalFieldsParams, detailsParams } = this.requestParams({
            ...changeFields,
            rankTypeCn: formValues?.rankTypeCn,
            rankTypeRow: formValues?.rankTypeRow,
            starCnValue: formValues?.starCnValue,
            starRowValue: formValues?.starRowValue,
            starCnExpiredAt: formValues?.starCnExpiredAt,
            starRowExpiredAt: formValues?.starRowExpiredAt,
          });
          if (
            internalFieldsParams.bookingJourney &&
            internalFieldsParams.bookingJourney === this.props.property.bookingJourney
          ) {
            delete internalFieldsParams.bookingJourney;
          }
          if (Object.keys(internalFieldsParams).length > 0) {
            requests.push(
              fetch(
                queries.updatePropertyInternalFields(
                  Object.assign(internalFieldsParams, { id: property.id }),
                ),
              ),
            );
          }
          if (Object.keys(detailsParams).length > 0) {
            requests.push(
              fetch(
                queries.updatePropertyDetails(Object.assign(detailsParams, { id: property.id })),
              ),
            );
          }
          if (isSemiOrInstant && (hasTenancyAgreementChanged || isCreateAction)) {
            const data = this.buildPropertyTenancyAgreementData(
              propertyTenancyAgreementChangeParams,
              isCreateAction,
            );
            changeFields.tenancyAgreement = data;
            const query = isCreateAction
              ? queries.createPropertyTenancyAgreement(data)
              : queries.updatePropertyTenancyAgreement(data);
            requests.push(fetch(query));
          }

          const changeParams = Object.assign({}, changeFields);

          if (Object.keys(internalFieldsParams.profile).length) {
            changeParams.profile = { ...internalFieldsParams.profile };
          }

          // These values are located in changeParams.profile, so remove them from the outside
          [
            'rankTypeCn',
            'starCnValue',
            'starCnExpiredAt',
            'rankTypeRow',
            'starRowValue',
            'starRowExpiredAt',
          ].forEach(item => {
            if (item in changeParams) {
              delete changeParams[item];
            }
          });

          // Some params'value is different with the response, can't directly save to redux.
          [
            {
              queryUseName: 'displaySettings',
            },
          ].forEach(item => {
            if (item.queryUseName === 'displaySettings' && changeParams[item.queryUseName]) {
              // Convert form values to the format expected by the API, case and delimiters are not important
              changeParams[item.queryUseName] = changeParams[item.queryUseName]?.map(item => ({
                locale: item,
              }));
            }
          });

          Promise.all(requests)
            .then(res => {
              if (res.length > 0 && Object.values(res[0])[0] && Object.values(res[0])[0].property) {
                const { drafts } = Object.values(res[0])[0].property;
                changeParams.drafts = drafts;
              }
              callback({ status: 'success', isUpdated: true });
            })
            .catch(e => {
              callback({ status: 'err', err: e });
            })
            .finally(() => {
              this.props.afterSave(changeParams);
            });
        } else {
          callback({ status: 'success', isUpdated: false });
        }
      },
    );
  };

  buildPropertyTenancyAgreementData = (data, isCreateAction = false) => {
    const params = { ...data };
    const defaultParams = {};

    if (isCreateAction) {
      defaultParams.propertyId = this.props.property.id;
      defaultParams.timeType = propertyTenancyAgreementTimeType.ANYTIME;
      defaultParams.calculateType = propertyTenancyAgreementCalculateType.BEFORE;
      defaultParams.field = propertyTenancyAgreementField.MOVE_IN_DATE;
    } else {
      defaultParams.id = this.mergedProperty.tenancyAgreement.id;
    }

    if (params.timeType === propertyTenancyAgreementTimeType.FIXED) {
      defaultParams.unit = propertyTenancyAgreementUnit.DAY;
      defaultParams.value = 1;
    } else if (params.timeType === propertyTenancyAgreementTimeType.ANYTIME) {
      params.unit = null;
      params.value = null;
    }
    return Object.assign(defaultParams, params);
  };

  handleBlurNumberInput = field => {
    if (this.props.form.getFieldValue(field) === null) {
      this.props.form.setFieldsValue({ [field]: 0 });
    }
  };

  onEnTabBlur = () => {
    setTimeout(() => {
      this.props.form.validateFields(['descriptionCn', 'headlineCn'], { force: true });
    }, 100);
  };

  onCnTabBlur = () => {
    setTimeout(() => {
      this.props.form.validateFields(['description', 'headline'], { force: true });
    }, 100);
  };

  isValidated = () => {
    const fieldsError = this.props.form.getFieldsError();
    return Object.values(fieldsError).filter(error => error).length === 0;
  };

  getDisplaySettingsSelectState = () => {
    const { form } = this.props;
    const displaySettings = form.getFieldValue('displaySettings');
    if (displaySettings.length === displayLocaleOptions.length) {
      return 'All';
    }
    if (displaySettings.length === 0) {
      return 'None';
    }
    return 'Some';
  };

  handleDisplaySettingsSelectAll = e => {
    this.props.form.setFieldsValue({
      displaySettings: e.target.checked
        ? displayLocaleOptions.map(option => option.value.toUpperCase().replace('-', '_'))
        : [],
    });
  };

  render() {
    const {
      t,
      property,
      form: { getFieldDecorator, getFieldValue },
      rejectedDraftIds,
      showBookingJourneyWarning,
      handleBookingTypeChange,
    } = this.props;
    const { Option } = Select;
    // 判断是SEMI_AUTOMATIC还是INSTANT_BOOK
    const isSemiOrInstant = ['SEMI_AUTOMATIC', 'INSTANT_BOOK'].includes(
      this.mergedProperty?.bookingJourney,
    );

    const isSemiOrInstantOrManual = ['SEMI_AUTOMATIC', 'INSTANT_BOOK', 'MANUAL'].includes(
      this.mergedProperty?.bookingJourney,
    );

    const allListings = property.unitTypes.edges.reduce(
      (prev, curr) => [...prev, ...(curr?.node?.listings ?? [])],
      [],
    );

    const isListingAllSoldOut = allListings?.every(
      item => item?.availability === propertyState.SOLD_OUT,
    );

    const isBookingTypeDisabled = (() => {
      if (
        [propertyStatus.NEW, propertyStatus.EDITING, propertyStatus.UNPUBLISHED].includes(
          property.status,
        )
      ) {
        return false;
      }

      if (property.status === propertyStatus.PUBLISHED) {
        if (
          [bookingJourneyType.MANUAL, bookingJourneyType.FULL_AUTOMATIC].includes(
            property.bookingJourney,
          )
        ) {
          return true;
        }

        return !isListingAllSoldOut;
      }

      return true;
    })();

    const isFullAutomaticOptDisabled = (() => {
      if (property.bookingJourney !== bookingJourneyType.FULL_AUTOMATIC) {
        if (property.status === propertyStatus.PUBLISHED) {
          return isListingAllSoldOut && !property.externalId;
        } else {
          return !property.externalId;
        }
      } else {
        return false;
      }
    })();

    const isStripePayment = this.isStripePayment(property);
    const isDepositOnly = this.isDepositOnly(property);

    const disabledApCategory = isStripePayment || isDepositOnly;

    const isPaymentOnlineNo = !property?.paymentPlan?.paymentOnline;

    const displaySettingsInitial = this.mergedProperty.displaySettings?.map(item =>
      item.locale.toUpperCase().replace('-', '_'),
    );

    return (
      <PropertyDetailWrapper
        t={t}
        isPublished={property.status === propertyStatus.PUBLISHED}
        onClickSave={this.beforeSave}
        rejectedDraftIds={rejectedDraftIds}
        expirePropertyDraft={this.props.expirePropertyDraft}
        isValidated={this.isValidated()}
        property={property}
        canEdit={!onlyViewPropertyRole()}
      >
        <Form className="property-detail">
          <p className="property-detail__subtitle">
            {t('cms.properties.edit.detail.basic_information')}
          </p>
          <Row>
            <Col span={24}>
              <div className="property-detail__basic-info">
                <span className="property-detail__basic-label">
                  {t('cms.properties.edit.detail.landlord')}
                </span>
                <span className="property-detail__basic-text property-detail__basic-text--ellipsis">
                  {this.mergedProperty.landlord ? this.mergedProperty.landlord.name : null}
                </span>
                <span className="property-detail__line" />
                <span className="property-detail__basic-label">
                  {t('cms.properties.edit.detail.City')}
                </span>
                <span className="property-detail__basic-text">{this.getCityCountry()}</span>
                <span className="property-detail__line" />
                <span className="property-detail__basic-label">
                  {t('cms.properties.edit.detail.currency')}
                </span>
                <span className="property-detail__basic-text">
                  {this.mergedProperty
                    ? `${this.mergedProperty.currency} - ${getSymbolFromCurrency(
                        this.mergedProperty.currency,
                      )}`
                    : '-'}
                </span>
                <span className="property-detail__line" />
                <span className="property-detail__basic-label">
                  {t('cms.properties.edit.detail.billing_cycle')}
                </span>
                <span className="property-detail__basic-text">
                  {this.mergedProperty
                    ? getBillingCycleText(this.mergedProperty.billingCycle, billingCycleOptions)
                    : '-'}
                </span>
              </div>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <FormItem label={t('cms.properties.edit.detail.property_name')} colon={false}>
                {getFieldDecorator('name', {
                  rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                  initialValue: this.mergedProperty.name,
                  validateTrigger: 'onBlur',
                })(
                  <Input
                    placeholder={t('cms.properties.edit.detail.property_name.placeholder')}
                    onBlur={this.onNameBlur}
                  />,
                )}
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem label={t('cms.properties.edit.detail.property_slug')} colon={false}>
                {getFieldDecorator('slug', {
                  rules: [
                    { required: true, message: t('cms.properties.edit.error.blank') },
                    { validator: this.validateSlug },
                  ],
                  initialValue: this.mergedProperty.slug,
                  validateTrigger: 'onBlur',
                })(
                  <Input placeholder={t('cms.properties.edit.detail.property_slug.placeholder')} />,
                )}
              </FormItem>
            </Col>
          </Row>
          <Row gutter={24} style={{ marginBottom: '8px' }}>
            <Col span={12}>
              <FormItem
                label={t('cms:cms.properties.edit.detail.property_type')}
                colon={false}
                required
              >
                <Input
                  value={
                    this.mergedProperty.propertyType ? this.mergedProperty.propertyType.name : null
                  }
                  disabled
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                label={t('cms:cms.properties.edit.detail.resident_type')}
                colon={false}
                required
              >
                <Input
                  value={
                    this.mergedProperty.residentType
                      ? t(
                          `cms:cms.properties.edit.detail.resident_type.${this.mergedProperty.residentType}`,
                        )
                      : null
                  }
                  disabled
                />
              </FormItem>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <FormItem label={t('cms.properties.edit.detail.beds_number')} colon={false}>
                {getFieldDecorator('totalBeds', {
                  initialValue: this.mergedProperty.totalBeds,
                  trigger: 'onChange',
                  validateTrigger: 'onBlur',
                })(
                  <InputNumber
                    min={0}
                    placeholder={t('cms.properties.edit.detail.beds_number.placeholder')}
                    precision={0}
                    onBlur={() => this.handleBlurNumberInput('totalBeds')}
                  />,
                )}
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                label={
                  <span>
                    {t('cms.properties.edit.detail.booking_type')}
                    <Tooltip
                      title={t(
                        [bookingJourneyType.MANUAL, bookingJourneyType.FULL_AUTOMATIC].includes(
                          property.bookingJourney,
                        )
                          ? 'cms.properties.edit.detail.booking_type.tooltip_manual'
                          : 'cms.properties.edit.detail.booking_type.tooltip',
                      )}
                      placement="top"
                    >
                      <QuestionCircleOutlined
                        style={{ marginLeft: 5, color: 'rgba(0, 0, 0, .45)' }}
                      />
                    </Tooltip>
                  </span>
                }
                colon={false}
                extra={
                  showBookingJourneyWarning && (
                    <span style={{ color: '#F9A600' }}>
                      {t('cms.properties.edit.detail.booking_type.warning')}
                    </span>
                  )
                }
              >
                {getFieldDecorator('bookingJourney', {
                  initialValue: this.mergedProperty.bookingJourney,
                  rules: [
                    {
                      required: true,
                      message: t('cms.properties.edit.error.blank'),
                    },
                  ],
                  trigger: 'onChange',
                  validateTrigger: 'onBlur',
                })(
                  <SelectBookingType
                    disabled={isBookingTypeDisabled}
                    disabledOption={
                      isFullAutomaticOptDisabled ? bookingJourneyType.FULL_AUTOMATIC : null
                    }
                    onChange={handleBookingTypeChange}
                  />,
                )}
              </FormItem>
            </Col>
          </Row>

          <CancellationBlock form={this.props.form} t={t} mergedProperty={this.mergedProperty} />

          <Row gutter={24}>
            <Col span={12}>
              <FormItem label={t('cms.free_cancellation_policy.title')} colon={false}>
                {getFieldDecorator('cancellationProcess', {
                  initialValue: htmlMinify(this.mergedProperty.cancellationProcess),
                  validateTrigger: 'onChange',
                })(
                  <ReactQuill
                    placeholder={t('cms.properties.edit.detail.cancelation_policy.plcaeholder')}
                    fontTypes={[3, false]}
                  />,
                )}
                {this.quillCounter(getFieldValue('cancellationProcess'))}
              </FormItem>
            </Col>
          </Row>

          <If condition={isSemiOrInstant}>
            <p className="property-detail__subtitle">
              {t('cms.properties.edit.detail.tenancy_agreement')}
            </p>
            <Row gutter={24}>
              <Col span={12}>
                <Row gutter={4}>
                  <Col span={24} className="property-detail__tenancy-agreement-time-type-container">
                    <label className="property-detail__tenancy-agreement-time-type-label">
                      {t('cms.properties.edit.detail.tenancy_agreement.label')}
                    </label>
                    <FormItem className="property-detail__tenancy-agreement-time-type">
                      {getFieldDecorator('tenancyAgreementTimeType', {
                        valuePropName: 'checked',
                        initialValue:
                          property.tenancyAgreement &&
                          property.tenancyAgreement.timeType &&
                          property.tenancyAgreement.timeType ===
                            propertyTenancyAgreementTimeType.FIXED,
                      })(
                        <Checkbox className="property-detail__tenancy-agreement-time-label">
                          {t('cms.properties.edit.detail.tenancy_agreement.checkbox.label')}
                        </Checkbox>,
                      )}
                    </FormItem>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <If condition={getFieldValue('tenancyAgreementTimeType')}>
                    <Col span={5} className="property-detail__tenancy-agreement-container">
                      <FormItem>
                        {getFieldDecorator('tenancyAgreementValue', {
                          initialValue:
                            property.tenancyAgreement && property.tenancyAgreement.value
                              ? property.tenancyAgreement.value
                              : 1,
                          rules: [{ required: true, message: ' ' }],
                        })(
                          <InputNumber
                            min={1}
                            max={365}
                            precision={0}
                            className="property-detail__tenancy-agreement-value"
                          />,
                        )}
                      </FormItem>
                    </Col>
                    <Col span={5}>
                      <FormItem>
                        {getFieldDecorator('tenancyAgreementUnit', {
                          validateTrigger: 'onChange',
                          initialValue:
                            property.tenancyAgreement && property.tenancyAgreement.unit
                              ? property.tenancyAgreement.unit
                              : propertyTenancyAgreementUnit.DAY,
                        })(
                          <Select disabled className="property-detail__tenancy-agreement-unit">
                            <Option value={propertyTenancyAgreementUnit.DAY}>
                              {t('cms.properties.edit.detail.tenancy_agreement.unit.day')}
                            </Option>
                          </Select>,
                        )}
                      </FormItem>
                    </Col>
                  </If>
                  <If condition={!getFieldValue('tenancyAgreementTimeType')}>
                    <Col span={10}>
                      <FormItem>
                        <Input
                          disabled
                          value={t('cms.properties.edit.detail.tenancy_agreement.value.anytime')}
                        />
                      </FormItem>
                    </Col>
                  </If>
                  <Col span={7}>
                    <FormItem>
                      {getFieldDecorator('tenancyAgreementCalculateType', {
                        validateTrigger: 'onChange',
                        initialValue:
                          property.tenancyAgreement && property.tenancyAgreement.calculateType
                            ? property.tenancyAgreement.calculateType
                            : propertyTenancyAgreementCalculateType.BEFORE,
                      })(
                        <Select className="property-detail__tenancy-agreement-calculate-type">
                          <For of={this.propertyTenancyAgreementCalculateTypeList} each="type">
                            <Option
                              value={type}
                              key={type}
                              className="property-detail__tenancy-agreement-calculate-option"
                            >
                              {t(
                                `cms.properties.edit.detail.tenancy_agreement.calculate_type.option.${toLower(
                                  type,
                                )}`,
                              )}
                            </Option>
                          </For>
                        </Select>,
                      )}
                    </FormItem>
                  </Col>
                  <Col span={7}>
                    <FormItem>
                      {getFieldDecorator('tenancyAgreementField', {
                        validateTrigger: 'onChange',
                        initialValue:
                          property.tenancyAgreement && property.tenancyAgreement.calculateType
                            ? property.tenancyAgreement.field
                            : propertyTenancyAgreementField.MOVE_IN_DATE,
                      })(
                        <Select disabled className="property-detail__tenancy-agreement-field">
                          <Option value={propertyTenancyAgreementField.MOVE_IN_DATE}>
                            {t('cms.properties.edit.detail.tenancy_agreement.field.move_in_date')}
                          </Option>
                        </Select>,
                      )}
                    </FormItem>
                  </Col>
                </Row>
              </Col>
            </Row>
          </If>

          <p className="property-detail__subtitle">
            {t('cms.properties.edit.detail.property_description')}
          </p>
          <Row gutter={24}>
            <Col span={12}>
              <div className="property-detail__description-wrap">
                <FormItem label={t('cms.properties.edit.detail.headline_cn')} colon={false}>
                  {getFieldDecorator('headlineCn', {
                    initialValue: this.mergedProperty.headlineCn
                      ? this.mergedProperty.headlineCn
                      : undefined,
                    rules: [{ max: 120, message: ' ' }],
                    validateTrigger: 'onChange',
                  })(
                    <TextArea
                      style={{ height: '48px' }}
                      placeholder={t('cms.properties.edit.detail.headline.place_holder')}
                      onBlur={this.onCnTabBlur}
                      className="property-detail__headline-textarea"
                    />,
                  )}
                  {this.textAreaCounter(getFieldValue('headlineCn'), 120)}
                </FormItem>
                <FormItem label={t('cms.properties.edit.detail.desc_cn')} colon={false}>
                  {getFieldDecorator('descriptionCn', {
                    initialValue: htmlMinify(this.mergedProperty.descriptionCn),
                    normalize: this.changeRichText,
                  })(
                    <ReactQuill
                      placeholder={t('cms.properties.edit.detail.desc.placeholder')}
                      fontTypes={[3, false]}
                      onBlur={this.onCnTabBlur}
                    />,
                  )}
                  {this.quillCounter(getFieldValue('descriptionCn'))}
                </FormItem>
              </div>
            </Col>
            <Col span={12}>
              <div className="property-detail__description-wrap">
                <FormItem label={t('cms.properties.edit.detail.headline_en')} colon={false}>
                  {getFieldDecorator('headline', {
                    initialValue: this.mergedProperty.headline
                      ? this.mergedProperty.headline
                      : undefined,
                    rules: [{ max: 300, message: ' ', required: false }],
                    validateTrigger: 'onChange',
                  })(
                    <TextArea
                      style={{ height: '48px' }}
                      placeholder={t('cms.properties.edit.detail.headline.place_holder')}
                      onBlur={this.onEnTabBlur}
                      className="property-detail__headline-textarea"
                    />,
                  )}
                  {this.textAreaCounter(getFieldValue('headline'), 300)}
                </FormItem>
                <FormItem label={t('cms.properties.edit.detail.desc_en')} colon={false}>
                  {getFieldDecorator('description', {
                    initialValue: htmlMinify(this.mergedProperty.description),
                    normalize: this.changeRichText,
                  })(
                    <ReactQuill
                      placeholder={t('cms.properties.edit.detail.desc.placeholder')}
                      fontTypes={[3, false]}
                      onBlur={this.onEnTabBlur}
                    />,
                  )}
                  {this.quillCounter(getFieldValue('description'))}
                </FormItem>
              </div>
            </Col>
          </Row>

          <If condition={isSemiOrInstantOrManual}>
            <p className="property-detail__subtitle">
              {t('cms.properties.edit.detail.cashflow_management')}
            </p>

            <If condition={isPaymentOnlineNo}>
              <div className="property-detail__paymentOnline-box">
                <Icon
                  type="exclamation-circle"
                  theme="filled"
                  style={{
                    color: 'rgba(107, 146, 243, 1)',
                  }}
                />
                <span>{t('cms.property.details.not_payment_online.cashflow.hint')}</span>
              </div>
            </If>
            <If condition={isDepositOnly || isStripePayment}>
              <div className="property-detail__hint-box">
                <p className="property-detail__stripe-tip">
                  {t('cms.landlord.property.bulk_edit.first.hint')}
                </p>
                <ul style={{ margin: 0, paddingLeft: '12px' }}>
                  <If condition={isStripePayment}>
                    <li className="property-detail__stripe-tip">
                      {t('cms.properties.edit.detail.stripe_hint')}
                    </li>
                  </If>
                  <If condition={isDepositOnly}>
                    <li className="property-detail__stripe-tip">
                      {t('cms.landlord.property.bulk_deposit_only.hint')}
                    </li>
                  </If>
                </ul>
              </div>
            </If>

            <Row gutter={24}>
              <Col span={12}>
                <FormItem
                  label={
                    <span>
                      {t('cms.properties.edit.detail.ap_category')}
                      <Tooltip
                        title={
                          <div>
                            {getSafeHtml(t('cms.properties.edit.detail.ap_category.tooltip'))}
                          </div>
                        }
                        placement="top"
                      >
                        <QuestionCircleOutlined
                          style={{ marginLeft: 5, color: 'rgba(0, 0, 0, .45)' }}
                        />
                      </Tooltip>
                    </span>
                  }
                  colon={false}
                >
                  <div
                    ref={node => {
                      this.apCategoryNode = node;
                    }}
                  >
                    {getFieldDecorator('apCategory', {
                      rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                      initialValue: this.mergedProperty.apCategory
                        ? this.mergedProperty.apCategory.toUpperCase()
                        : apCategoryEnum[0],
                    })(
                      <Select disabled={disabledApCategory}>
                        {apCategoryEnum.map(item => (
                          <Option value={item}>
                            {t(`cms.landlord.detail.ap_category.option.${item.toLowerCase()}`)}
                          </Option>
                        ))}
                      </Select>,
                    )}
                  </div>
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  label={t('cms.properties.edit.detail.deposit_transfer_time')}
                  colon={false}
                >
                  <div
                    ref={node => {
                      this.depositTransferTime = node;
                    }}
                  >
                    {getFieldDecorator('depositTransferTime', {
                      rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                      initialValue: this.mergedProperty.depositTransferTime
                        ? this.mergedProperty.depositTransferTime.toUpperCase()
                        : depositTransferTime[0],
                    })(
                      <Select disabled={isStripePayment}>
                        {depositTransferTime.map(item => (
                          <Option value={item}>
                            {t(`cms.landlord.detail.ap_category.deposit.${item.toLowerCase()}`)}
                          </Option>
                        ))}
                      </Select>,
                    )}
                  </div>
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem label={t('cms.properties.edit.detail.rent_transfer_time')} colon={false}>
                  <div
                    ref={node => {
                      this.rentTransferTime = node;
                    }}
                  >
                    {getFieldDecorator('rentTransferTime', {
                      rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                      initialValue: this.mergedProperty.rentTransferTime
                        ? this.mergedProperty.rentTransferTime.toUpperCase()
                        : rentTransferTime[0],
                    })(
                      <Select disabled={isStripePayment}>
                        {rentTransferTime.map(item => (
                          <Option value={item}>
                            {t(`cms.landlord.detail.ap_category.rent.${item.toLowerCase()}`)}
                          </Option>
                        ))}
                      </Select>,
                    )}
                  </div>
                </FormItem>
              </Col>
            </Row>
          </If>

          {/* SRP Ranking Setting */}
          <p className="property-detail__subtitle">
            {t('cms.properties.edit.detail.srp_ranking_setting')}
          </p>
          <p className="property-detail__description">
            {t('cms.properties.edit.detail.srp_ranking_description')}.
          </p>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label={t('cms.properties.edit.others.ranking_value')}>
                {getFieldDecorator('rank', {
                  initialValue: this.mergedProperty.rank,
                })(
                  <InputNumber
                    min={0}
                    max={1000}
                    precision={0}
                    placeholder={t('cms.properties.edit.others.ranking_value.placeholder')}
                    onBlur={() => this.handleBlurNumberInput('rank')}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <RankSetting
                t={t}
                locale="cn"
                getFieldDecorator={getFieldDecorator}
                items={{
                  rankType: this.rankSettings.rankTypeCn,
                  starValue: this.rankSettings.starCnValue,
                  starExpiredAt: this.rankSettings.starCnExpiredAt,
                }}
              />
            </Col>

            <Col span={12}>
              <RankSetting
                t={t}
                locale="row"
                getFieldDecorator={getFieldDecorator}
                items={{
                  rankType: this.rankSettings.rankTypeRow,
                  starValue: this.rankSettings.starRowValue,
                  starExpiredAt: this.rankSettings.starRowExpiredAt,
                }}
              />
            </Col>
          </Row>

          {/* Display Setting */}
          <div className="property-detail__section">
            <p className="property-detail__subtitle">
              {t('cms.properties.edit.detail.display_setting')}
            </p>
            <p className="property-detail__description">
              {t('cms.properties.edit.detail.display_setting.description')}.
            </p>

            <Row gutter={24}>
              <Col span={24}>
                <Form.Item required label={t('cms.properties.edit.detail.display_setting.label')}>
                  {getFieldDecorator('displaySettings', {
                    initialValue: displaySettingsInitial || [],
                    rules: [
                      {
                        require: true,
                        validator: (_rule, value, callback) => {
                          if (value.length === 0) {
                            return callback(t('cms.listing.modal.error_message.can_not_empty'));
                          }
                          return callback();
                        },
                      },
                    ],
                  })(
                    <Select
                      mode="multiple"
                      className="property-detail__display-setting__select"
                      placeholder={t('cms.properties.edit.detail.display_setting.placeholder')}
                      dropdownRender={menu => (
                        <div>
                          <div
                            className="property-detail__display-setting__select-all-container"
                            onMouseDown={e => e.preventDefault()}
                          >
                            <Checkbox
                              className="property-detail__display-setting__select"
                              indeterminate={this.getDisplaySettingsSelectState() === 'Some'}
                              onChange={this.handleDisplaySettingsSelectAll}
                              checked={this.getDisplaySettingsSelectState() === 'All'}
                            >
                              {t('cms.properties.edit.detail.display_setting.select_all')}
                            </Checkbox>
                          </div>
                          <Divider className="property-detail__display-setting__divider" />
                          {menu}
                        </div>
                      )}
                    >
                      {displayLocaleOptions.map(item => (
                        <Option value={item.value.toUpperCase().replace('-', '_')} key={item.value}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>,
                  )}
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </PropertyDetailWrapper>
    );
  }
}

function RankSetting({ t, locale, getFieldDecorator, items }) {
  const dateFormat = dayFormatType.UKFORMAT;
  const [showStarConfig, setShowStarConfig] = useState(items.rankType === 'STAR');

  const disabledDate = useCallback(current => {
    return !current.isSameOrAfter(moment().startOf('day'));
  }, []);
  return (
    <div className="property-detail__rank-setting">
      <Form.Item
        label={
          locale === 'cn'
            ? t('cms.properties.edit.others.ranking_type_cn')
            : t('cms.properties.edit.others.ranking_type_row')
        }
      >
        <div className="property-detail__ranking-type-wrap">
          {getFieldDecorator(locale === 'cn' ? 'rankTypeCn' : 'rankTypeRow', {
            initialValue: items.rankType,
          })(
            <Radio.Group
              onChange={e => {
                if (e.target.value === 'STAR') {
                  setShowStarConfig(true);
                } else {
                  setShowStarConfig(false);
                }
              }}
            >
              <For each="option" of={rankTypeOptions} index="key">
                <Radio value={option.value} key={key}>
                  {t(option.text)}
                </Radio>
              </For>
            </Radio.Group>,
          )}
        </div>
      </Form.Item>

      <If condition={showStarConfig}>
        <Form.Item required label={t(`cms.properties.edit.others.star_value_${locale}`)}>
          <div className="property-detail__ranking-type-wrap">
            {getFieldDecorator(locale === 'cn' ? 'starCnValue' : 'starRowValue', {
              initialValue: items.starValue,
              rules: [
                {
                  require: true,
                  validator: (_rule, value, callback) => {
                    if (typeof value !== 'number') {
                      return callback(t('cms.listing.modal.error_message.can_not_empty'));
                    }
                    if (value > 0 && value <= 100) {
                      return callback();
                    } else {
                      return callback(t('cms.properties.edit.others.ranking_value.range_error'));
                    }
                  },
                },
              ],
            })(
              <InputNumber
                precision={0}
                placeholder={t('cms.properties.edit.others.ranking_value.placeholder')}
              />,
            )}
          </div>
          <p className="property-detail__ranking-tip">
            <Icon type="exclamation-circle" />
            {t('cms.properties.edit.others.ranking_value.tip')}
          </p>
        </Form.Item>

        <Form.Item label={t(`cms.properties.edit.others.expiration_date_${locale}`)}>
          {getFieldDecorator(locale === 'cn' ? 'starCnExpiredAt' : 'starRowExpiredAt', {
            initialValue: items.starExpiredAt ? moment(items.starExpiredAt) : null,
          })(
            <DatePicker
              disabledDate={disabledDate}
              style={{ width: '100%' }}
              format={dateFormat}
              showToday={false}
            />,
          )}
        </Form.Item>
      </If>
    </div>
  );
}

PropertyDetail.propTypes = {
  t: PropTypes.func.isRequired,
  property: PropTypes.object,
  showBookingJourneyWarning: PropTypes.bool,
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
    setFields: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    validateFieldsAndScroll: PropTypes.func.isRequired,
    getFieldsError: PropTypes.func.isRequired,
  }),
  afterSave: PropTypes.func.isRequired,
  changeFields: PropTypes.object.isRequired,
  onRef: PropTypes.func.isRequired,
  rejectedDraftIds: PropTypes.array,
  expirePropertyDraft: PropTypes.func,
  checkDraftReviewmodal: PropTypes.func,
  setNeedConfirmRoomListing: PropTypes.func,
  handleBookingTypeChange: PropTypes.func,
};

PropertyDetail.defaultProps = {
  t: () => {},
  property: {},
  showBookingJourneyWarning: false,
  afterSave: () => {},
  changeFields: {},
  onRef: () => {},
  rejectedDraftIds: [],
  expirePropertyDraft: () => {},
  checkDraftReviewmodal: () => {},
  setNeedConfirmRoomListing: () => {},
  handleBookingTypeChange: () => {},
};
